'use client'

import { HTMLProps, useState } from 'react'
import clsx from 'classnames'
import { useLocale } from 'use-intl'
import { useTranslations } from 'next-intl'
import { Link, usePathname } from '@/routing'
import { useHeaderChangeColor } from '@/shared/utils'
import Logo from '@/public/logo-type.svg'
import LogoEng from '@/public/logo-type-eng.svg'
import MenuIcon from '@/public/header-menu.svg'
import CrossIcon from '@/public/cross.svg'
import SearchIcon from '@/public/header-search.svg'
import { IMenuItem } from '@/interfaces/menu'
import { LANGUAGES } from '@/shared/constants/stubs'
import { DropdownMenu, MenuItem } from '@/shared/ui/dropdown-menu'
import { Dialog, DialogTrigger } from '@/sections/search/dialog'
import { Search } from '@/sections/search'
import { HeaderMenu } from './ui/header-menu'
import style from './header.module.scss'

interface HeaderProps extends HTMLProps<HTMLDivElement> {
  navItems: IMenuItem[]
}

export const Header = (props: HeaderProps) => {
  const { className, navItems, children } = props
  const pathname = usePathname()
  const currentLocale = useLocale()
  const translate = useTranslations()
  const isHomePage = pathname !== '/'
  const [isOpen, setIsOpen] = useState(false)
  useHeaderChangeColor()
  const toggleNoScroll = (open: boolean) => {
    if (open) {
      document.body.style.overflowY = 'hidden'
    } else {
      document.body.style.overflowY = 'initial'
    }
  }
  const handleOpenModal = (opened?: boolean) => {
    setIsOpen(prev => {
      const newState = opened ?? !prev
      toggleNoScroll(newState)

      return newState
    })
  }

  const currentFlag = LANGUAGES.map(({ locale, icon: FlagIcon }) => {
    if (currentLocale !== locale) {
      return
    }
    return <FlagIcon key={locale} className={clsx(style.icon, style.langIcon)} />
  })

  return (
    <header
      id="header"
      className={clsx(className, style.header, {
        [style.isHomePage]: isHomePage,
        [style.isOpenModal]: isOpen,
      })}
    >
      <div className={clsx(style.inner, 'main-padding')}>
        <Link className={style.logo} href="/" onClick={() => handleOpenModal(false)}>
          {currentLocale === 'ru' ? <Logo /> : <LogoEng />}
        </Link>
        <nav className={clsx(style.navigation, className, 'text')}>
          {navItems.map(({ name, url, is_external, is_folder, items }, index) => {
            if (is_folder) {
              const isPathInclude = pathname === url || items.some(({ url }) => pathname === url)

              return (
                <DropdownMenu
                  className={clsx(style.navigationLink, style.navigationLinkFolder, 'text text_type_d', {
                    [style.isPathInclude]: isPathInclude,
                  })}
                  label={name}
                  key={name}
                >
                  {items?.map(({ name, url, is_external }) => {
                    const isActiveNavLink = pathname === url

                    return (
                      <MenuItem key={name}>
                        <Link
                          className={clsx(style.menuItemLink, 'text text_type_d text_color_dark-070')}
                          data-open={isActiveNavLink ? '' : undefined}
                          href={url ?? ''}
                          key={name}
                          target={is_external ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                        >
                          {name}
                        </Link>
                      </MenuItem>
                    )
                  })}
                </DropdownMenu>
              )
            }

            const isActiveNavLink = pathname.startsWith(url ?? '')
            return (
              <Link
                className={clsx(style.navigationLink, 'text_type_d')}
                data-open={isActiveNavLink ? '' : undefined}
                href={url ?? ''}
                key={name}
                target={is_external ? '_blank' : '_self'}
                rel="noopener noreferrer"
              >
                {name}
              </Link>
            )
          })}
        </nav>
        <ul className={style.actions}>
          <DropdownMenu placement="bottom" className={clsx(style.actionItem)} label={currentFlag}>
            {LANGUAGES.map(({ label, icon: LangIcon, locale }) => (
              <MenuItem key={label}>
                <Link
                  className={clsx(style.langItemLink, 'text text_type_d text_color_dark-070', {
                    [style.currentLocale]: currentLocale === locale,
                  })}
                  href={pathname}
                  replace
                  locale={locale as 'ru' | 'en' | 'zh' | 'pt' | 'fr' | 'ar' | 'fa'}
                >
                  <LangIcon className={style.langIcon} />
                  {label}
                </Link>
              </MenuItem>
            ))}
          </DropdownMenu>
          <Dialog>
            <DialogTrigger className={clsx(style.icon, style.actionItem, style.searchIcon)}>
              <SearchIcon />
            </DialogTrigger>
            <Search placeholder={translate('inputPlaceholderGlobalSearch')} />
          </Dialog>
          <button className={clsx(style.actionItem, style.menuIcon)} onClick={() => handleOpenModal()}>
            {isOpen ? <CrossIcon className={clsx(style.icon)} /> : <MenuIcon className={clsx(style.icon)} />}
          </button>
        </ul>
      </div>
      <HeaderMenu
        onOpenModal={handleOpenModal}
        open={isOpen}
        onClick={event => {
          const element = event.target as HTMLDivElement

          if (element.classList.contains('menu-close-trigger-js')) {
            handleOpenModal(false)
          }
        }}
      >
        {children}
      </HeaderMenu>
    </header>
  )
}
